import React, { useRef, useState } from 'react';
import { FileText, Upload, Trash2, CheckCircle2, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { sellersAPI } from '../../services/api';

const openBlob = async (loader) => {
  try {
    const blob = await loader();
    window.open(URL.createObjectURL(blob), '_blank', 'noopener');
  } catch { toast.error('Document indisponible'); }
};

const DocRow = ({ kind, label, doc, required, disabled, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const r = await sellersAPI.uploadDocument(kind, file); onChange(r.seller); toast.success('Document transmis'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  const remove = async () => {
    setBusy(true);
    try { const r = await sellersAPI.deleteDocument(doc.id); onChange(r.seller); } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); } finally { setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`seller-doc-${kind}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${doc ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
        {doc ? <CheckCircle2 size={18} /> : <FileText size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label} {required && <span className="text-orange-600">*</span>}</p>
        <p className="text-xs text-gray-500 truncate" data-testid={`seller-doc-${kind}-name`}>{doc ? `${doc.filename} · ${(doc.size / 1024).toFixed(0)} Ko` : 'PDF, JPG, PNG ou WebP — 8 Mo max.'}</p>
      </div>
      <div className="flex items-center gap-1">
        {doc && <button type="button" onClick={() => openBlob(() => sellersAPI.openDocument(doc.id))} className="p-2 text-gray-500 hover:text-gray-900" aria-label="Voir" data-testid={`seller-doc-${kind}-view`}><Eye size={16} /></button>}
        {!disabled && (
          <>
            <input ref={input} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`seller-doc-${kind}-input`} />
            <button type="button" disabled={busy} onClick={() => input.current?.click()} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-gray-300 px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid={`seller-doc-${kind}-upload`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {doc ? 'Remplacer' : 'Ajouter'}
            </button>
            {doc && <button type="button" disabled={busy} onClick={remove} className="p-2 text-gray-400 hover:text-red-600" aria-label="Supprimer" data-testid={`seller-doc-${kind}-delete`}><Trash2 size={15} /></button>}
          </>
        )}
      </div>
    </li>
  );
};

// Liste des justificatifs requis selon l'activité (+ pièces facultatives)
const SellerDocuments = ({ seller, docKinds, disabled, onChange }) => {
  const required = seller.required_documents || [];
  const optional = ['assurance_rc', 'other'];
  const byKind = Object.fromEntries((seller.documents || []).map((d) => [d.kind, d]));
  return (
    <section className="bg-white rounded-xl shadow-sm p-5" data-testid="seller-documents">
      <h3 className="font-bold text-gray-900">Justificatifs</h3>
      <p className="text-xs text-gray-500 mt-1">Documents demandés selon votre activité. Ils ne sont visibles que par vous et par l&rsquo;équipe OTOPIECES.</p>
      {!seller.activity_type && <p className="mt-3 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="seller-docs-choose-activity">Choisissez d&rsquo;abord votre type d&rsquo;activité et enregistrez le brouillon.</p>}
      <ul className="divide-y divide-gray-100 mt-2">
        {required.map((k) => <DocRow key={k} kind={k} label={docKinds[k] || k} doc={byKind[k]} required disabled={disabled} onChange={onChange} />)}
        {optional.map((k) => <DocRow key={k} kind={k} label={docKinds[k] || k} doc={byKind[k]} disabled={disabled} onChange={onChange} />)}
      </ul>
    </section>
  );
};

export default SellerDocuments;
